import "../styles/SalesOrders.css"

import { useEffect, useState, useRef } from "react"
import { useNavigate } from "react-router-dom"

import Page from "../components/Page"
import useFetchWithMsal from "../hooks/useFetchWithMsal"
import { getStates } from "../utils/salesUtils"

import { DataGrid, gridClasses, GridToolbar } from "@mui/x-data-grid"
import {
  Dialog,
  Box,
  Tooltip,
  Chip,
  TextField,
  Button,
  Stack,
  LinearProgress,
  createTheme,
  ThemeProvider,
  DialogTitle,
  Typography,
  MenuItem,
} from "@mui/material"

import OpenInNewIcon from "@mui/icons-material/OpenInNew"

const theme = createTheme({
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "&.Mui-disabled .MuiOutlinedInput-input": {
            color: "#000", // Fallback for non-WebKit browsers
            WebkitTextFillColor: "#000", // Key fix for Chrome/Safari
          },
          "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
            borderColor: "#cecece",
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          "&.Mui-disabled": {
            color: "#1976d2",
          },
        },
      },
    },
  },
})

const data_URL = "/api/sales/orders"

const states = getStates()

export const SalesOrdersPage = () => {
  const [focusedRow, setFocusedRow] = useState(null)
  const dateRef = useRef(null);
  const navigate = useNavigate()

  const [editableValues, setEditableValues] = useState({
      salesOrderTypeId:  "",
      distributor:  "",
      rName:  "",
      rStreet:  "",
      rCity:  "",
      rState:  "",
      rZip:  "",
      rCountry:  "",
      notes:  "",
      rEmail:  "",
      pickupOrder:  false,
  })
  const [isEditing, setIsEditing] = useState(false)

  const editableFields = Object.keys(editableValues)

  const { execute: executeGetOrders, data, isLoading } = useFetchWithMsal()
  const { execute: executeGetOrderById } = useFetchWithMsal()
  const { execute: executeUpdateSalesOrder, isLoading: posting } = useFetchWithMsal()
  const { execute: executeGetReferenceData, data: salesReferenceData, isLoading: isLoadingReferenceData } = useFetchWithMsal()

  const updateSalesOrder = async (id, data) => {
    try {
      const resp = await executeUpdateSalesOrder(
        "POST",
        `/api/sales/update/${id}`,
        data
      )

      if (resp.error) {
        alert("Error updating order")
        return
      }
    } catch (error) {
      console.error("Error updating sales order:", error)
    }
    executeGetOrders("GET", "/api/sales/orders")
  }

  useEffect(() => {
    if (!salesReferenceData && !isLoadingReferenceData) {
      executeGetReferenceData("GET", "/api/sales/md").then((resp) => {})
    }
  }, [executeGetReferenceData, salesReferenceData, isLoadingReferenceData])

  useEffect(() => {
      if (focusedRow) {
        setEditableValues({
          salesOrderTypeId: focusedRow.salesOrderTypeId || "",
          distributor: focusedRow.distributor || "",
          requiredDeliveryDate: focusedRow.requiredDeliveryDate || "",
          rName: focusedRow.rName || "",
          rStreet: focusedRow.rStreet || "",
          rCity: focusedRow.rCity || "",
          rState: focusedRow.rState || "",
          rZip: focusedRow.rZip || "",
          rCountry: focusedRow.rCountry || "",
          notes: focusedRow.notes || "",
          rEmail: focusedRow.rEmail || "",
          pickupOrder: focusedRow.pickupOrder || false,
        })
      }
    }, [focusedRow])

  useEffect(() => {
    if (!data && !isLoading) {
      executeGetOrders("GET", "/api/sales/orders")
    }
  }, [executeGetOrders, data, isLoading])

  return (
    <Page fullHeight className="SalesOrdersPage">
      {isLoading || posting && (
        <div className="loadingBar">
          <LinearProgress />
        </div>
      )}
      <div className="ribbon">
        <Stack direction="row" spacing={1}>
          <div>Your Orders</div>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            onClick={() => navigate("/orders/create")}
            endIcon={<OpenInNewIcon />}
          >
            New Order
          </Button>
        </Stack>
      </div>
      <div className="body">
        <div
          style={{
            width: "100%",
            height: "100%",
            flex: 1,
            maxHeight: "100%",
          }}
          className="gridContainer"
        >
          <DataGrid
            rows={data || []}
            columns={[
              {
                field: "id",
                headerName: "ID",
                width: 60,
              },
              {
                field: "dateOfRequest",
                headerName: "Submitted",
                width: 100,
              },
              {
                field: "requiredDeliveryDate",
                headerName: "Delivery",
                width: 100,
              },
              {
                field: "status",
                headerName: "Status",
                width: 140,
                renderCell: (params) => {
                  const status = params.row.salesorderstatusId
                  const color =
                    status === 2 || status === 7
                      ? "warning"
                      : status === 1
                      ? "success"
                      : status === 5
                      ? "info"
                      : "error"
                  return (
                    <Chip
                      color={color}
                      label={params.row.status}
                      size="small"
                    />
                  )
                },
              },
              {
                field: "rName",
                headerName: "Recipient",
                maxWidth: 300,
                flex: 1,
              },
              {
                field: "products",
                headerName: "Products",
                width: 350,
                renderCell: (params) => {
                  const items = params.row?.salesOrderItems || []
                  const productNames = items.map((item) =>
                    item?.sku ? item.sku : item?.productId || "N/A"
                  )

                  const previewCount = 2 // Number of products to show before "+N more"
                  const preview = productNames.slice(0, previewCount)
                  const hiddenCount = productNames.length - preview.length

                  const tooltipTitle = productNames.join(", ")

                  return (
                    <Tooltip title={tooltipTitle}>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {preview.map((name, index) => (
                          <Chip key={index} label={name} size="small" />
                        ))}
                        {hiddenCount > 0 && (
                          <Chip label={`+${hiddenCount} more`} size="small" />
                        )}
                      </Box>
                    </Tooltip>
                  )
                },
              },
              {
                field: "rState",
                headerName: "State",
                width: 100,
              },
            ]}
            sortModel={[
              {
                field: "id",
                sort: "desc",
              },
            ]}
            hideFooterSelectedRowCount={true}
            disableDensitySelector
            slotProps={{
              toolbar: {
                showQuickFilter: true,
              },
            }}
            slots={{ toolbar: GridToolbar }}
            pagination
            getRowHeight={() => "auto"}
            getEstimatedRowHeight={() => 40}
            columnHeaderHeight={40}
            pageSizeOptions={[100]}
            sx={{
              [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]:
                {
                  outline: "none",
                },
              [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
                {
                  outline: "none",
                },
            }}
              onRowClick={async (params) => {
                const id = params?.row?.id;
                if (!id) {
                  console.warn("No row id on click:", params);
                  return;
                }

                try {
                  const order = await executeGetOrderById("GET", data_URL+`?id=${id}`);
                  if (!order) {
                    console.warn("No order returned for id:", id);
                    setFocusedRow({});
                    return;
                  }
                  setFocusedRow(order);
                  console.log("Focused row set:", order);
                } catch (err) {
                  console.error("Failed to load order:", err);
                  setFocusedRow({});
                  // optionally show a toast here
                }
              }}
            />
          </div>
        </div>

      {focusedRow && (
        <Dialog
          fullWidth
          maxWidth="lg"
          open={!!focusedRow}
          onClose={() => {
            setFocusedRow(null)
          }}
        >
          <Box sx={{ padding: "10px" }}>
                        <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "#f5f5f5",
                padding: "16px 24px",
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <Box>
                <Typography variant="h6">
                  Order #{focusedRow.id} -{" "}
                  {salesReferenceData?.salesOrderStatuses.find(
                    (type) => type.id === focusedRow?.salesorderstatusId
                  )?.name ?? "Unknown Status"}
                </Typography>
                <Typography variant="subtitle2">
                  {"Tracking Number: "}{focusedRow.trackingNumber ? focusedRow.trackingNumber : "N/A"}
                </Typography>
                <Typography variant="subtitle2">
                  {"Delivery Method: "}{focusedRow?.pickupOrder ? "Pickup" : "Shipping"}
                </Typography>
                <Typography variant="subtitle2">
                  Order Created: {focusedRow.dateOfRequest}
                </Typography>
              </Box>
              <div style={{ display: "flex", gap: "10px" }}>
                <Button
                  variant={isEditing ? "contained" : "outlined"}
                  onClick={() => {
                    if (isEditing) {
                      // Check for changes and update only if needed
                      const changedFields = {}

                      editableFields.forEach((field) => {
                        changedFields[field] = editableValues[field]
                      })

                      if (Object.keys(changedFields).length > 0) {
                        updateSalesOrder(focusedRow.id, changedFields) 
                        setFocusedRow({ ...focusedRow, ...changedFields })                      
                      }
                    }
                    setIsEditing(!isEditing)
                  }}
                >
                  {isEditing ? "Save" : "Edit"}
                </Button>
                <Button
                  variant="outlined"
                  disabled={!isEditing}
                  color="error"
                  onClick={() => {
                    // Reset changes
                    setEditableValues({
                      salesOrderTypeId: focusedRow.salesOrderTypeId || "",
                      distributor: focusedRow.distributor || "",
                      rName: focusedRow.rName || "",
                      rStreet: focusedRow.rStreet || "",
                      rCity: focusedRow.rCity || "",
                      rState: focusedRow.rState || "",
                      rZip: focusedRow.rZip || "",
                      rCountry: focusedRow.rCountry || "",
                      notes: focusedRow.notes || "",
                      rEmail: focusedRow.rEmail || "",
                      pickupOrder: focusedRow.pickupOrder || false,
                    })
                    setIsEditing(false)
                  }}
                >
                  Cancel
                </Button>
              </div>
            </DialogTitle>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <h6 style={{ marginTop: "10px", marginLeft: "10px" }}>Recipient Details</h6>
              {/* Recipient Details */}
              <div
                style={{
                  marginTop: "10px",
                  display: "flex",
                  gap: "10px",
                  alignItems: "flex-start",
                }}
              >
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <ThemeProvider theme={theme}>
                    <TextField
                      disabled={!isEditing}
                      variant="outlined"
                      size="small"
                      label="Distributor"
                      value={editableValues.distributor}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => setEditableValues({ ...editableValues, distributor: e.target.value })}
                    />
                    <TextField
                      onChange={(e) => setEditableValues({ ...editableValues, requiredDeliveryDate: e.target.value})}
                      onClick={() => isEditing && dateRef.current?.showPicker?.()}
                      disabled={!isEditing}
                      variant="outlined"
                      size="small"
                      type="date"
                      label="Delivery Date"
                      value={editableValues.requiredDeliveryDate ?? ''}
                      InputLabelProps={{ shrink: true }}
                      inputRef={dateRef}
                    />
                    <TextField
                      disabled={!isEditing}
                      variant="outlined"
                      size="small"
                      label="Order Type"
                      value={editableValues.salesOrderTypeId ?? ""}
                      InputLabelProps={{ shrink: true }}
                      select
                      onChange={(e) =>
                        setEditableValues({
                          ...editableValues,
                          salesOrderTypeId: e.target.value,
                        })
                      }
                    >
                      <MenuItem value={""}></MenuItem>
                      {salesReferenceData?.salesOrderTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.description}
                      </MenuItem>
                    ))}
                  </TextField>
                  </ThemeProvider>
                </div>
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <ThemeProvider theme={theme}>
                    <TextField
                      disabled={!isEditing}
                      variant="outlined"
                      size="small"
                      label="Recipient Name"
                      value={editableValues.rName || ""}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => setEditableValues({ ...editableValues, rName: e.target.value })}
                    />
                    <TextField
                      disabled={!isEditing}
                      variant="outlined"
                      size="small"
                      label="Recipient Email"
                      value={editableValues.rEmail}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => setEditableValues({ ...editableValues, rEmail: e.target.value })}
                    />
                    <TextField
                      disabled={!isEditing}
                      onChange={(e) => setEditableValues({ ...editableValues, pickupOrder: e.target.value })}
                      variant="outlined"
                      size="small"
                      label="Delivery Method"
                      value={editableValues.pickupOrder}
                      InputLabelProps={{ shrink: true }}
                      select
                    >
                      <MenuItem value={false}>Shipping</MenuItem>
                      <MenuItem value={true}>Pickup</MenuItem>
                    </TextField>
                  </ThemeProvider>
                </div>
              </div>
            </Box>

            {/* Address & Notes */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px", 
                marginTop: "10px",        
              }}
            >
              <div className="body" style={{ display: "grid", gap: "10px" }}>
                <h6 style={{ marginTop: "10px", marginLeft: "10px" }}>Shipping Address</h6>
                <ThemeProvider theme={theme}>
                  <TextField
                    disabled={!isEditing}
                    onChange={(e) => setEditableValues({ ...editableValues, rStreet: e.target.value })}
                    variant="outlined"
                    size="small"
                    label="Street"
                    value={editableValues.rStreet || ""}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                  disabled={!isEditing}
                    onChange={(e) => setEditableValues({ ...editableValues, rCity: e.target.value })}
                    variant="outlined"
                    size="small"
                    label="City"
                    value={editableValues.rCity || ""}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    select
                    label="State"
                    value={editableValues?.rState || ""}
                    size="small"
                    onChange={(e) =>
                      setEditableValues({
                        ...editableValues,
                        rState: e.target.value,
                      })
                    }
                    disabled={!isEditing}
                  >
                    {states.map((state) => (
                      <MenuItem key={state.value} value={state.value}>
                        {state.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    onChange={(e) => setEditableValues({ ...editableValues, rZip: e.target.value })}
                    disabled={!isEditing}
                    variant="outlined"
                    size="small"
                    label="Zip Code"
                    value={editableValues.rZip || ""}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    disabled={!isEditing}
                    onChange={(e) => setEditableValues({ ...editableValues, rCountry: e.target.value })}
                    variant="outlined"
                    size="small"
                    label="Country"
                    value={editableValues.rCountry || ""}
                    InputLabelProps={{ shrink: true }}
                  />
                </ThemeProvider>
              </div>

              {/* Notes */}
              <ThemeProvider theme={theme}>
                <h6 style={{ marginTop: "10px", marginLeft: "10px" }}>Additional Information</h6>
                <TextField
                  disabled={!isEditing}
                  onChange={(e) => setEditableValues({ ...editableValues, notes: e.target.value })}
                  variant="outlined"
                  size="small"
                  label="Notes"
                  multiline
                  rows={3}
                  value={editableValues.notes || ""}
                  InputLabelProps={{ shrink: true }}
                />
              </ThemeProvider>

              <hr style={{ marginTop: "10px" }} />

              {/* Products Table */}
              <div className="body" style={{ flex: 1 }}>
                <h6 style={{ margin: 0 }}>Products</h6>
                <DataGrid
                  rows={focusedRow.salesOrderItems || []}
                  columns={[
                    { field: "sku", headerName: "Product", width: 150 },
                    { field: "cases", headerName: "Cases", width: 100 },
                    { field: "bottles", headerName: "Bottles", width: 100 },
                    {
                      field: "shippedSku",
                      headerName: "Shipped SKU",
                      width: 150,
                    },
                  ]}
                  sx={{
                    "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                      display: "none",
                    },
                  }}
                  disableColumnMenu
                  hideFooter
                  disableRowSelectionOnClick
                  disableSelectionOnClick
                  autoHeight
                />
              </div>
            </div>
          </Box>
        </Dialog>
      )}
    </Page>
  )
}
